// src/components/GradedSellPicker.js
// Graded slabs in Sell To Us (TKT-2026-107322).
//
// A customer at the kiosk could only sell a raw card: there was no way to say
// "this is a PSA 10" and get the store's slab offer. The buylist site has had a
// Graded option per game for a long time, gated on the game's "Graded slabs"
// switch. The kiosk follows the same switch, through the buylist's own
// customer-facing flag (store-config gameConfigs[cat].gradedEnabled), which
// the Dashboard receives as gradedCategoryIds. There is no kiosk setting.
//
// The customer picks a grader and a grade from the lists the pricing engine
// prices by (utils/buylistGraded), and may give the slab's cert number. A
// grade the store makes no offer on reads "Not offered" and cannot be added.
// The line goes into the cart as its own variant: the grade is its condition,
// the grader and cert ride beside it, and it has no SKU, so a PSA 10 and a
// BGS 10 of one card are two lines. The offer it carries is only what the
// screen showed: the buylist re-quotes every slab line with its grader when
// the trade-in is submitted, and refuses one that was over.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKioskTheme } from '../contexts/KioskThemeContext';
import KioskKeyboard from './KioskKeyboard';
import {
    GRADED_COMPANIES,
    GRADED_TIER_CONDITIONS,
    DEFAULT_GRADER,
    DEFAULT_TIER,
    cleanCertNumber,
    fetchGradedQuotes,
    gradedOffer,
    gradedPaymentLegs,
    isGradedOffered,
    resolveGradedPayment,
} from '../utils/buylistGraded';

// Held as long as a raw quote is (Dashboard BUYLIST_PRICE_TTL_MS): the buylist
// re-quotes at submit, so an old figure is one the store may no longer make.
const GRADED_QUOTE_TTL_MS = 10 * 60 * 1000;
const gradedQuoteCache = new Map();

const cachedGradedQuotes = (key) => {
    const hit = gradedQuoteCache.get(key);
    if (!hit) return undefined;
    if (Date.now() - hit.at > GRADED_QUOTE_TTL_MS) {
        gradedQuoteCache.delete(key);
        return undefined;
    }
    return hit.data;
};

/**
 * The state behind one Sell To Us slab: grader, grade, cert, the store's
 * answers, and the cart line they make. Asks the buylist only while `active`,
 * so a card that never leaves Raw costs nothing.
 */
export function useGradedSell({ storeName, productId, printing, language, active, gameSettings, categoryId }) {
    const [grader, setGrader] = useState(DEFAULT_GRADER);
    const [tier, setTier] = useState(DEFAULT_TIER);
    const [certNumber, setCertNumber] = useState('');
    const [result, setResult] = useState({ key: null, data: null });

    // The dialog stays mounted between products: each starts from the defaults.
    useEffect(() => {
        setGrader(DEFAULT_GRADER);
        setTier(DEFAULT_TIER);
        setCertNumber('');
    }, [productId]);

    const key = active && storeName && productId && printing
        ? `${storeName}|${productId}|${printing}|${language || 'EN'}`
        : null;

    useEffect(() => {
        if (!key) return undefined;
        const hit = cachedGradedQuotes(key);
        if (hit !== undefined) {
            setResult({ key, data: hit });
            return undefined;
        }
        let cancelled = false;
        fetchGradedQuotes(storeName, { productId, printing, language: language || 'EN' })
            .then((data) => {
                // An empty answer is not held, so a failed ask is asked again.
                if (data) gradedQuoteCache.set(key, { at: Date.now(), data });
                if (!cancelled) setResult({ key, data });
            })
            .catch(() => {
                if (!cancelled) setResult({ key, data: null });
            });
        return () => { cancelled = true; };
    }, [key, storeName, productId, printing, language]);

    // Answers belong to the product, printing and language they were asked for.
    const settled = key !== null && result.key === key;
    const quotes = settled ? result.data : null;
    const loading = key !== null && !settled;

    const offer = useMemo(() => gradedOffer(quotes, grader, tier), [quotes, grader, tier]);
    const offered = isGradedOffered(offer);
    const offerFor = useCallback((t) => gradedOffer(quotes, grader, t), [quotes, grader]);
    const legs = useMemo(() => gradedPaymentLegs(gameSettings, categoryId), [gameSettings, categoryId]);

    /** { variant, options } for onAdd, or null when this slab cannot be added. */
    const buildLine = useCallback((paymentType) => {
        if (loading || !offered) return null;
        const resolved = resolveGradedPayment(paymentType, offer, legs);
        if (!resolved) return null;
        return {
            variant: {
                skuId: null,
                printing,
                condAbbr: tier,
                langAbbr: language || 'EN',
                gradingCompany: grader,
                certNumber: cleanCertNumber(certNumber),
            },
            options: {
                mode: 'buy',
                paymentType: resolved,
                buylistCashPrice: offer.cash,
                buylistCreditPrice: offer.credit,
                buylistLadder: null,
            },
        };
    }, [loading, offered, offer, legs, printing, tier, language, grader, certNumber]);

    return {
        grader, setGrader, tier, setTier, certNumber, setCertNumber,
        quotes, loading, settled, offer, offered, offerFor, buildLine,
    };
}

/** Raw or Graded. Shown only on a game whose "Graded slabs" is on. */
export function SellTypeToggle({ value, onChange, compact = false }) {
    const theme = useTheme();
    return (
        <Box onClick={(e) => e.stopPropagation()}>
            <ToggleButtonGroup
                value={value}
                exclusive
                onChange={(_, v) => v && onChange(v)}
                size='small'
                fullWidth
                aria-label='Card type'
                sx={{
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        ...(compact ? { fontSize: '0.7rem', py: 0.5, minHeight: 32 } : { borderRadius: 2, minHeight: 40 }),
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${theme.palette.success.main} !important`,
                        color: `${theme.palette.success.contrastText} !important`,
                    },
                }}
            >
                <ToggleButton value='raw'>Raw</ToggleButton>
                <ToggleButton value='graded'>Graded</ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
}

SellTypeToggle.propTypes = {
    value: PropTypes.oneOf(['raw', 'graded']).isRequired,
    onChange: PropTypes.func.isRequired,
    compact: PropTypes.bool,
};

const noop = () => {};

// The grid card opens the detail dialog on a click, and on Enter or Space
// (which it also preventDefaults, so a Space typed in the cert field would
// vanish). Those stop here; every other key, Escape among them, goes on.
const stopCardKeys = (e) => {
    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
};

/**
 * Grader, grade and optional cert for one slab. `graded` is useGradedSell's
 * return.
 */
export default function GradedSellPicker({ graded, paymentType = 'Cash', compact = false }) {
    const theme = useTheme();
    const { formatMoney, themeOverrides } = useKioskTheme();
    const onScreenKeyboard = themeOverrides?.onScreenKeyboardEnabled === true;
    const [keyboardOpen, setKeyboardOpen] = useState(false);

    const toggleSx = {
        '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            ...(compact
                ? { fontSize: '0.65rem', py: 0.5, px: 0.5, minWidth: 0, minHeight: 32 }
                : { borderRadius: 2, minWidth: 48, minHeight: 44, flexDirection: 'column', py: 0.75, lineHeight: 1.2 }),
        },
        '& .Mui-selected': {
            backgroundColor: `${theme.palette.success.main} !important`,
            color: `${theme.palette.success.contrastText} !important`,
        },
        '& .graded-not-offered': {
            opacity: 0.45,
            textDecoration: 'line-through',
        },
    };

    const { setCertNumber } = graded;
    const typeIntoCert = useCallback((ch) => setCertNumber((prev) => cleanCertNumber(prev + ch)), [setCertNumber]);
    const backspaceCert = useCallback(() => setCertNumber((prev) => prev.slice(0, -1)), [setCertNumber]);

    return (
        <Box
            onClick={(e) => e.stopPropagation()}
            onKeyDown={stopCardKeys}
            sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1.5 }}
        >
            <Box>
                {!compact && (
                    <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                        Grader
                    </Typography>
                )}
                <ToggleButtonGroup
                    value={graded.grader}
                    exclusive
                    onChange={(_, v) => v && graded.setGrader(v)}
                    size='small'
                    fullWidth
                    aria-label='Grader'
                    sx={toggleSx}
                >
                    {GRADED_COMPANIES.map((g) => <ToggleButton key={g} value={g}>{g}</ToggleButton>)}
                </ToggleButtonGroup>
            </Box>

            <Box>
                {!compact && (
                    <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                        Grade
                    </Typography>
                )}
                <ToggleButtonGroup
                    value={graded.tier}
                    exclusive
                    onChange={(_, v) => v && graded.setTier(v)}
                    size='small'
                    fullWidth
                    aria-label='Grade'
                    sx={toggleSx}
                >
                    {GRADED_TIER_CONDITIONS.map((t) => {
                        const tierOffer = graded.offerFor(t);
                        const notOffered = graded.settled && !isGradedOffered(tierOffer);
                        const preview = paymentType === 'Credit' ? tierOffer.credit : tierOffer.cash;
                        return (
                            <ToggleButton
                                key={t}
                                value={t}
                                className={notOffered ? 'graded-not-offered' : undefined}
                                aria-label={notOffered ? `${t}, not offered` : t}
                            >
                                <span>{compact ? t.replace(/^Grade\s+/, '') : t}</span>
                                {!compact && graded.settled && (
                                    <Typography variant='caption' sx={{ fontSize: '0.6rem', opacity: 0.85, mt: 0.15 }}>
                                        {notOffered ? 'Not offered' : formatMoney(preview)}
                                    </Typography>
                                )}
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>
            </Box>

            <TextField
                label='Cert # (optional)'
                size='small'
                fullWidth
                value={graded.certNumber}
                onChange={(e) => graded.setCertNumber(cleanCertNumber(e.target.value))}
                inputProps={{
                    maxLength: 40,
                    'aria-label': 'Cert number (optional)',
                    ...(onScreenKeyboard ? { inputMode: 'none' } : {}),
                }}
                onFocus={onScreenKeyboard ? () => setKeyboardOpen(true) : undefined}
            />
            {onScreenKeyboard && keyboardOpen && (
                <KioskKeyboard
                    layout='tel'
                    onKey={typeIntoCert}
                    onBackspace={backspaceCert}
                    onDone={() => setKeyboardOpen(false)}
                    onToggleShift={noop}
                    onToggleSymbols={noop}
                />
            )}
        </Box>
    );
}

GradedSellPicker.propTypes = {
    graded: PropTypes.shape({
        grader: PropTypes.string.isRequired,
        setGrader: PropTypes.func.isRequired,
        tier: PropTypes.string.isRequired,
        setTier: PropTypes.func.isRequired,
        certNumber: PropTypes.string.isRequired,
        setCertNumber: PropTypes.func.isRequired,
        settled: PropTypes.bool.isRequired,
        offerFor: PropTypes.func.isRequired,
    }).isRequired,
    paymentType: PropTypes.oneOf(['Cash', 'Credit']),
    compact: PropTypes.bool,
};
